import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import dayjs from "dayjs";
import * as moment from "moment-timezone";
import { useAppContextNonNull } from "@contexts/AppContext";
import { buildRestUrl, buildRestUrlWithQuery, buildAuthHeadersForFetch } from "../../../services/RestService";
import RestEndpointsEnum from "../../../models/RestEndpointsEnum";
import PaymentTypesEnum from "../../../models/PaymentTypesEnum";
import IDaily_Route from "../../../models/IDaily_Route";
import IOnGetEodReconciliationsDTO, { IEodReconciliationDetail } from "../../../models/DTO/IOnGetEodReconciliationsDTO";
import IOnGetDailyRoutePaymentsDTO, { IDailyRoutePaymentLine } from "../../../models/DTO/IOnGetDailyRoutePaymentsDTO";
import IOnGetDailyRouteTransfersDTO, { IDailyRouteTransferRow } from "../../../models/DTO/IOnGetDailyRouteTransfersDTO";
import HelperService from "../../../services/HelperService";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwButton from "../../shared/OwButton/OwButton";
import Breadcrumbs from "../../shared/Breadcrumbs/Breadcrumbs";
import "./DailyRouteEodReconciliation.scss";

export const DailyRouteEodReconciliation = () => {
    const { guid } = useParams<{ guid: string }>();
    const { userModel, supplierModel } = useAppContextNonNull();

    const [dailyRoute, setDailyRoute] = useState<IDaily_Route | null>(null);
    const [eod, setEod] = useState<IEodReconciliationDetail | null>(null);
    const [payments, setPayments] = useState<IDailyRoutePaymentLine[]>([]);
    const [transfers, setTransfers] = useState<IDailyRouteTransferRow[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        if (!guid) return;

        setIsLoading(true);
        setError(null);

        try {
            const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

            const routeUrl = buildRestUrl({
                endpoint: RestEndpointsEnum.DailyRoute,
                params: { dailyRouteGuid: guid },
            });

            // The order payments are route-scoped and independent of the vehicle/EOD
            // lookup, so fetch them concurrently. Non-fatal on failure — the SKU/cash
            // reconciliation still renders without them.
            const paymentsUrl = buildRestUrl({
                endpoint: RestEndpointsEnum.DailyRoutePayments,
                params: { dailyRouteGuid: guid },
            });
            const paymentsPromise = fetch(paymentsUrl, { method: "GET", headers });
            // Mark the promise handled now: if the route/EOD fetches below throw first, this one is
            // never awaited and a rejection would surface as an unhandled rejection. The side handler
            // leaves the original promise's rejection intact for the await + try/catch below.
            paymentsPromise.catch(() => {});

            // Warehouse transfers that touched the route's van (driver load/unload
            // bookings) — same non-fatal concurrent pattern as payments.
            const transfersUrl = buildRestUrl({
                endpoint: RestEndpointsEnum.DailyRouteTransfers,
                params: { dailyRouteGuid: guid },
            });
            const transfersPromise = fetch(transfersUrl, { method: "GET", headers });
            transfersPromise.catch(() => {});

            const routeResponse = await fetch(routeUrl, { method: "GET", headers });

            if (!routeResponse.ok) {
                throw new Error(`Failed to fetch route: ${routeResponse.statusText}`);
            }

            const routeData = await routeResponse.json();
            const route: IDaily_Route = routeData.dailyRoute || routeData;
            setDailyRoute(route);

            // The reconciliation is keyed by vehicle + business date, both of which the
            // route carries. Newest-first response; [0] is the latest for the vehicle-day.
            if (route?.Vehicle?.guid && route.route_date) {
                const eodUrl = buildRestUrlWithQuery({
                    endpoint: RestEndpointsEnum.GetEodReconciliations,
                    query: {
                        vehicleGuid: route.Vehicle.guid,
                        businessDate: dayjs(route.route_date).format("YYYY-MM-DD"),
                    },
                });

                const eodResponse = await fetch(eodUrl, { method: "GET", headers });

                if (!eodResponse.ok) {
                    throw new Error(`Failed to fetch EOD reconciliation: ${eodResponse.statusText}`);
                }

                const eodData: IOnGetEodReconciliationsDTO = await eodResponse.json();
                setEod(eodData.eod_reconciliations?.[0] || null);
            } else {
                setEod(null);
            }

            try {
                const paymentsResponse = await paymentsPromise;

                if (paymentsResponse.ok) {
                    const paymentsData: IOnGetDailyRoutePaymentsDTO = await paymentsResponse.json();
                    setPayments(paymentsData.payments || []);
                } else {
                    console.error("Failed to fetch route payments:", paymentsResponse.statusText);
                    setPayments([]);
                }
            } catch (paymentsErr) {
                console.error("Failed to fetch route payments:", paymentsErr);
                setPayments([]);
            }

            try {
                const transfersResponse = await transfersPromise;

                if (transfersResponse.ok) {
                    const transfersData: IOnGetDailyRouteTransfersDTO = await transfersResponse.json();
                    setTransfers(transfersData.transfers || []);
                } else {
                    console.error("Failed to fetch route transfers:", transfersResponse.statusText);
                    setTransfers([]);
                }
            } catch (transfersErr) {
                console.error("Failed to fetch route transfers:", transfersErr);
                setTransfers([]);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "An error occurred");
        } finally {
            setIsLoading(false);
        }
    }, [guid, userModel, supplierModel]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const varianceClass = (variance: number) => (variance < 0 ? "variance-negative" : variance > 0 ? "variance-positive" : "variance-zero");

    const formatQty = (value: number) => (Number.isInteger(value) ? String(value) : value.toFixed(2));

    const signedQty = (value: number) => (value > 0 ? `+${formatQty(value)}` : formatQty(value));

    const paymentTypeKey = (paymentTypeId: number | null): string => {
        switch (paymentTypeId) {
            case PaymentTypesEnum.CREDIT_CARD:
                return "credit_card";
            case PaymentTypesEnum.CASH:
                return "cash";
            case PaymentTypesEnum.CHECK:
                return "check";
            case PaymentTypesEnum.TERMS:
                return "net_terms";
            case PaymentTypesEnum.SALES_REP:
                return "sales_rep";
            default:
                return "payment";
        }
    };

    const formatReceivedAt = (receivedAt: string | Date | null): string => {
        if (!receivedAt) return "-";
        if (supplierModel.timezone) {
            return moment.utc(receivedAt).tz(supplierModel.timezone).format("h:mm A z");
        }
        return dayjs(receivedAt).format("h:mm A");
    };

    const paymentTotals = Object.entries(
        payments.reduce<Record<string, number>>((acc, payment) => {
            const key = paymentTypeKey(payment.payment_type_id);
            acc[key] = (acc[key] || 0) + payment.amount;
            return acc;
        }, {})
    );

    if (isLoading) {
        return (
            <div className="DailyRouteEodReconciliation">
                <div className="loading-state">
                    <i className="fas fa-spinner fa-spin fa-2x" />
                    <div className="mt-2">
                        <OwLabel title="loading" />
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="DailyRouteEodReconciliation">
                <div className="error-state">
                    <i className="fas fa-exclamation-triangle fa-2x text-danger" />
                    <div className="mt-2">{error}</div>
                    <OwButton title="retry" className="btn-primary mt-3" fn={fetchData} />
                </div>
            </div>
        );
    }

    if (!dailyRoute) {
        return (
            <div className="DailyRouteEodReconciliation">
                <div className="error-state">
                    <i className="fas fa-route fa-2x" />
                    <div className="mt-2">
                        <OwLabel title="route_not_found" />
                    </div>
                </div>
            </div>
        );
    }

    const submittedAt =
        eod?.submitted_at && supplierModel.timezone
            ? moment.utc(eod.submitted_at).tz(supplierModel.timezone).format("MMM D, YYYY h:mm A z")
            : eod?.submitted_at
              ? dayjs(eod.submitted_at).format("MMM D, YYYY h:mm A")
              : null;

    const hasCash = eod != null && (eod.cash_expected != null || eod.cash_counted != null);

    return (
        <div className="DailyRouteEodReconciliation">
            <Breadcrumbs
                crumbs={[
                    { title: "daily_routes", url: "/routes/daily" },
                    { title: dailyRoute.name || "Route Detail", url: `/routes/daily/${dailyRoute.guid}/detail`, skipTranslate: true },
                    { title: "eod_reconciliation", url: "" },
                ]}
            />

            <div className="detail-header mt-4">
                <div className="header-info">
                    <OwTitle title="eod_reconciliation" tag="h2" defaultMargins={false} />
                    <OwTitle title={dailyRoute.name || "Route"} tag="h4" disableTranslation={true} defaultMargins={false} />
                    <div className="route-date">
                        <i className="far fa-calendar-alt me-2" />
                        {dayjs(dailyRoute.route_date).format("dddd, MMMM D, YYYY")}
                    </div>
                    {dailyRoute.Vehicle && (
                        <div className="route-vehicle">
                            <i className="fas fa-truck me-2" />
                            {dailyRoute.Vehicle.name}
                        </div>
                    )}
                </div>
            </div>

            {!dailyRoute.Vehicle ? (
                <div className="empty-state">
                    <i className="fas fa-truck fa-2x mb-2" />
                    <div>
                        <OwLabel title="no_vehicle_assigned" />
                    </div>
                </div>
            ) : !eod ? (
                <div className="empty-state">
                    <i className="fas fa-clipboard-check fa-2x mb-2" />
                    <div>
                        <OwLabel title="eod_not_submitted_yet" />
                    </div>
                </div>
            ) : (
                <>
                    <div className="info-cards-row">
                        <div className="info-card summary-card">
                            <div className="card-header">
                                <i className="fas fa-clipboard-check me-2" />
                                <OwLabel title="eod_reconciliation" />
                                <span className={`eod-status-badge status-${eod.status || "completed"}`}>
                                    <i className="fas fa-check-circle me-1" />
                                    <OwLabel title={eod.status || "completed"} />
                                </span>
                            </div>
                            <div className="card-body">
                                {eod.submitted_by_user_name && (
                                    <div className="info-row">
                                        <i className="fas fa-user-tie me-2" />
                                        <OwLabel title="submitted_by" />: <span className="ms-1">{eod.submitted_by_user_name}</span>
                                    </div>
                                )}
                                {submittedAt && (
                                    <div className="info-row">
                                        <i className="far fa-clock me-2" />
                                        <OwLabel title="submitted_at" />: <span className="ms-1">{submittedAt}</span>
                                    </div>
                                )}
                                <div className="info-row">
                                    <i className="fas fa-not-equal me-2" />
                                    <OwLabel title="items_with_variance" />: <span className="ms-1">{eod.total_variance_count}</span>
                                </div>
                                <div className="info-row">
                                    <i className="fas fa-boxes me-2" />
                                    <OwLabel title="total_variance_qty" />:
                                    <span className={`ms-1 ${varianceClass(eod.total_variance_qty)}`}>
                                        {signedQty(eod.total_variance_qty)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {hasCash && (
                            <div className="info-card cash-card">
                                <div className="card-header">
                                    <i className="fas fa-money-bill-wave me-2" />
                                    <OwLabel title="cash" />
                                </div>
                                <div className="card-body">
                                    <div className="info-row">
                                        <OwLabel title="cash_expected" />:
                                        <span className="ms-1">
                                            {eod.cash_expected != null ? HelperService.formatCurrency({ value: eod.cash_expected }) : "-"}
                                        </span>
                                    </div>
                                    <div className="info-row">
                                        <OwLabel title="cash_counted" />:
                                        <span className="ms-1">
                                            {eod.cash_counted != null ? HelperService.formatCurrency({ value: eod.cash_counted }) : "-"}
                                        </span>
                                    </div>
                                    {eod.cash_variance != null && (
                                        <div className="info-row">
                                            <OwLabel title="cash_variance" />:
                                            <span className={`ms-1 ${varianceClass(eod.cash_variance)}`}>
                                                {eod.cash_variance > 0 ? "+" : ""}
                                                {HelperService.formatCurrency({ value: eod.cash_variance })}
                                            </span>
                                        </div>
                                    )}
                                    {eod.note && (
                                        <div className="info-row eod-note">
                                            <i className="fas fa-sticky-note me-2" />
                                            {eod.note}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="items-section">
                        <div className="items-header">
                            <div className="items-title">
                                <i className="fas fa-boxes me-2" />
                                <OwLabel title="items" />
                                <span className="items-count">({eod.items.length})</span>
                            </div>
                        </div>

                        <div className="items-list">
                            <div className="items-table-header">
                                <div className="col-item">
                                    <OwLabel title="item" />
                                </div>
                                <div className="col-sku">
                                    <OwLabel title="sku" />
                                </div>
                                <div className="col-qty">
                                    <OwLabel title="expected" />
                                </div>
                                <div className="col-qty">
                                    <OwLabel title="counted" />
                                </div>
                                <div className="col-qty">
                                    <OwLabel title="variance" />
                                </div>
                                <div className="col-reason">
                                    <OwLabel title="reason" />
                                </div>
                            </div>
                            {eod.items.map((item) => (
                                <div key={item.catalog_item_id} className={`item-row ${item.variance !== 0 ? "has-variance" : ""}`}>
                                    <div className="col-item">{item.catalog_item_name || `#${item.catalog_item_id}`}</div>
                                    <div className="col-sku">{item.catalog_item_sku || "-"}</div>
                                    <div className="col-qty">{formatQty(item.expected_qty)}</div>
                                    <div className="col-qty">{formatQty(item.counted_qty)}</div>
                                    <div className={`col-qty ${varianceClass(item.variance)}`}>{signedQty(item.variance)}</div>
                                    <div className="col-reason">{item.reason || ""}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}

            {transfers.length > 0 && (
                <div className="transfers-section">
                    <div className="transfers-header">
                        <div className="transfers-title">
                            <i className="fas fa-dolly me-2" />
                            <OwLabel title="warehouse_transfers" />
                            <span className="transfers-count">({transfers.length})</span>
                        </div>
                    </div>

                    <div className="transfers-list">
                        <div className="transfers-table-header">
                            <div className="col-transfer">
                                <OwLabel title="transfer" />
                            </div>
                            <div className="col-direction">
                                <OwLabel title="type" />
                            </div>
                            <div className="col-route">
                                <OwLabel title="route" />
                            </div>
                            <div className="col-qty">
                                <OwLabel title="items" />
                            </div>
                            <div className="col-qty">
                                <OwLabel title="qty" />
                            </div>
                            <div className="col-received">
                                <OwLabel title="received_at" />
                            </div>
                        </div>
                        {transfers.map((transfer) => (
                            <div key={transfer.guid} className="transfer-row">
                                <div className="col-transfer">
                                    <Link to={`/inventory/transfers/${transfer.guid}`} className="transfer-link">
                                        {transfer.name || transfer.guid}
                                        <i className="fas fa-external-link-alt ms-1" />
                                    </Link>
                                </div>
                                <div className="col-direction">
                                    <OwLabel title={transfer.direction === "load" ? "load_transfer" : "unload_transfer"} />
                                </div>
                                <div className="col-route">
                                    <i className={transfer.direction === "load" ? "fas fa-warehouse me-1" : "fad fa-truck me-1"} />
                                    {transfer.origin_warehouse_name}
                                    <i className="fas fa-arrow-right-long mx-2 opacity-50" />
                                    <i className={transfer.direction === "load" ? "fad fa-truck me-1" : "fas fa-warehouse me-1"} />
                                    {transfer.destination_warehouse_name}
                                </div>
                                <div className="col-qty">{transfer.item_count}</div>
                                <div className="col-qty">{formatQty(transfer.total_qty)}</div>
                                <div className="col-received">{formatReceivedAt(transfer.received_at)}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {payments.length > 0 && (
                <div className="payments-section">
                    <div className="payments-header">
                        <div className="payments-title">
                            <i className="fas fa-money-check-alt me-2" />
                            <OwLabel title="payments" />
                            <span className="payments-count">({payments.length})</span>
                        </div>
                        <div className="payments-totals">
                            {paymentTotals.map(([typeKey, total]) => (
                                <span key={typeKey} className="total-item">
                                    <OwLabel title={typeKey} />: {HelperService.formatCurrency({ value: total })}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div className="payments-list">
                        <div className="payments-table-header">
                            <div className="col-order">
                                <OwLabel title="order" />
                            </div>
                            <div className="col-customer">
                                <OwLabel title="customer" />
                            </div>
                            <div className="col-type">
                                <OwLabel title="type" />
                            </div>
                            <div className="col-amount">
                                <OwLabel title="amount" />
                            </div>
                            <div className="col-reference">
                                <OwLabel title="reference" />
                            </div>
                            <div className="col-received">
                                <OwLabel title="received_at" />
                            </div>
                        </div>
                        {payments.map((payment) => (
                            <div key={payment.payment_guid} className="payment-row">
                                <div className="col-order">
                                    {payment.order_guid ? (
                                        <Link to={`/orders/${payment.order_guid}/entry`} className="order-link">
                                            {payment.order_name || payment.order_guid}
                                            <i className="fas fa-external-link-alt ms-1" />
                                        </Link>
                                    ) : (
                                        payment.order_name || "-"
                                    )}
                                    {payment.is_cold_call && (
                                        <span className="cold-call-pill">
                                            <OwLabel title="cold_call" />
                                        </span>
                                    )}
                                </div>
                                <div className="col-customer">{payment.vendor_name || "-"}</div>
                                <div className="col-type">
                                    <OwLabel title={paymentTypeKey(payment.payment_type_id)} />
                                </div>
                                <div className="col-amount">{HelperService.formatCurrency({ value: payment.amount })}</div>
                                <div className="col-reference">{payment.reference || ""}</div>
                                <div className="col-received">{formatReceivedAt(payment.received_at)}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
